<template>
  <div>
    <Grid :headers="headers"
          :payload="{results, count, next, previous}"
          @api="getTours()"
          @filter="showFilterModal = true"
          @clearFilters="$refs.desktopFilters.reset()">
      <template #buttons>
        <base-button size="sm" action="import_tour" @click="$router.push({name: 'ToursImport'})">
          <base-icon icon="import" class="text-lg" />
        </base-button>
      </template>

      <template #filter>
        <ToursFilters ref="desktopFilters" />
      </template>

      <template #[`head.actions`]>
        <div class="bg-secondary"></div>
      </template>

      <template #[`body.name`]="{item}">
        <div class="flex flex-col">
          <div
            :class="[
              'flex flex-col lg:flex-row lg:items-center lg:space-x-2 leading-5 font-medium',
            ]"
          >
            <!-- will add item.parent as a condition -->
            <base-icon
              v-if="item.parent"
              v-tooltip="{content: item.parent === item.uuid ? 'Mix Parent' : 'Mixed'}"
              icon="link2"
              :class="[
                'p-0.5',
                item.parent === item.uuid ? 'text-blue-700 text-lg' : 'text-blue-500',
              ]"
            >
            </base-icon>
            <span
              v-if="item.is_discarded"
              class="bg-gray-600 rounded text-white text-xs px-1 py-0.5 max-w-min"
            >
              Discarded
            </span>

            <span
              :class="['truncate', item.is_discarded ? 'text-gray-500 ' : '']"
              v-tooltip="{
                content: item.name,
                placement: 'left',
              }"
              >{{ item.name }}</span
            >
          </div>
        </div>
      </template>

      <template #[`body.state`]="{item}">
        <span
          :class="[
            'px-2 inline-flex leading-6 text-xs font-semibold rounded text-white',
            stateColor(item.state),
          ]"
        >
          {{ item.state }}
        </span>
      </template>
      <template #[`body.created_at`]="{item}">
        <span class="inline-flex leading-6">{{
          item.state_changes.length ? new Date(item.state_changes[0].datetime).toDateString() : '-'
        }}</span>
      </template>
      <template #[`body.assigned_to__name`]="{item}">
        <div class="flex space-x-3 justify-between items-center w-44">
          <span class="truncate">
            {{ item.assigned_to !== null ? item.assigned_to : '-' }}
          </span>
          <base-button-icon
            icon="external-link"
            @click="openMixTour(item)"
            tooltip="Tablet Information"
            v-if="item.assigned_to !== null"
            size="sm"
            class="text-xs"
          />
        </div>
      </template>
      <template #[`body.type`]="{item}">
        <span>
          {{ item.type }}
        </span>
      </template>
      <template #[`body.tour_item_count`]="{item}">
        {{ item.tour_item_count }}
      </template>

      <template #[`body.actions`]="{item}">
        <div class="flex items-start justify-end space-x-3">
          <base-button-icon
            size="sm"
            tooltip="View more"
            icon="info"
            @click="handleDrawerClick(item)"
          />

          <a-popover
            overlayClassName="no-padding"
            :visible="clicked === item.uuid"
            @visibleChange="e => (clicked = e ? item.uuid : '')"
            placement="bottomLeft"
            trigger="click"
            arrowPointAtCenter
          >
            <template slot="content">
              <TourListActions
                :currentState="item.state"
                :isDiscarded="item.is_discarded"
                @changeState="updateState($event, item)"
                @delete="deleteTour(item)"
                @restore="restoreTour(item)"
              />
            </template>
            <base-button-icon
              size="sm"
              icon="more"
              :loading="isExporting === item.uuid || isDeleting === item.uuid"
            />
          </a-popover>
        </div>
      </template>
    </Grid>

    <!-- Mobile filters -->
    <transition v-if="showFilterModal" name="grow">
      <base-modal @close="showFilterModal = false" head="filters" bg="white" full-width>
        <ToursFilters :mobile="true" ref="mobileFilter" />
        <!-- Action buttons -->
        <div class="pt-4 mt-6 border-t flex flex-col justify-end w-full">
          <div class="flex flex-col">
            <div class="flex space-x-4">
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.reset()
                  showFilterModal = false
                "
                action="clear"
                context="secondary"
              >
                <base-icon icon="close" />
              </base-button>
              <base-button
                class="w-1/2"
                @click="
                  $refs.mobileFilter.submit()
                  showFilterModal = false
                "
                action="submit"
              >
                <base-icon icon="filters" />
              </base-button>
            </div>
          </div>
        </div>
      </base-modal>
    </transition>

    <TourAssign
      v-if="showTabletAssignmentModal"
      :tourSelected="selectedTourItem"
      :reassignment="selectedTourItem.assigned_to != null"
      @close="showTabletAssignmentModal = false"
      @change-tablet-name="closeTabletAssignmentModal"
      @success="1"
    />

    <router-view :key="$route.path" :tour="selectedTourItem" />

    <!-- Simulate Readouts for Demo -->
    <base-modal
      v-if="showModalSimulation"
      @close="showModalSimulation = false"
      head="Simulate Readouts"
    >
      <SimulateReadouts :tour="selectedTourItem" @close="showModalSimulation = false" />
    </base-modal>
    <TourMix
      v-if="showMixTour"
      :visible="showMixTour"
      :tablet="tablet"
      @close=";(showMixTour = false), getTours()"
    />
  </div>
</template>

<script>
import fileExport from '@/state/helpers/fileExport.js'
import Popover from 'ant-design-vue/lib/popover'
import 'ant-design-vue/lib/popover/style/index.css'
import {
  BaseSelect,
  BaseIcon,
  BaseInput,
  BaseButton,
  BaseFieldset,
  BaseButtonIcon,
  BaseRadio,
  BaseModal,
} from '@/components/base'
import Grid from '@/components/shared/Grid.vue'
import TourAssign from './TourAssign'
import TourDetailsDrawer from './TourDetailsDrawer'
import TourListActions from './TourListActions.vue'
import TourMix from './TourMix.vue'
import ToursFilters from './ToursFilters.vue';
import SimulateReadouts from './SimulateReadouts.vue'
import _baseCheck from '@/components/base/_base-check.vue'

export default {
  async mounted() {
    try {
      let allTablets = await this.$store.dispatch('tablets/getInitialTablets')
      this.tablets = allTablets.results
    } catch (error) {
      console.log(error)
    }
  },
  components: {
    TourDetailsDrawer,
    TourAssign,
    TourListActions,
    ToursFilters,
    SimulateReadouts,
    BaseSelect,
    Grid,
    BaseIcon,
    BaseInput,
    BaseButton,
    BaseFieldset,
    BaseButtonIcon,
    BaseRadio,
    BaseModal,
    'a-popover': Popover,
    _baseCheck,
    TourMix,
  },

  data() {
    return {
      tablets: [],
      tourList: {},
      selectedTourItem: null,
      showFilterModal: false,
      showTabletAssignmentModal: false,
      selectedTour: null,
      clicked: '',
      showModalSimulation: false,
      isExporting: null,
      isDeleting: null,
      discardedSwitch: false,
      onCreate: null,
      prevRoute: {},
      tablet: {},
      showMixTour: false,

      clickedTourActions: null,

      headers: {
        name: {
          label: 'name',
          order: true,
          className: 'w-4/12',
        },
        state: {
          label: 'state',
          order: true,
          className: 'w-2/12',
        },
        created_at: {
          label: 'imported',
          order: false,
          className: 'w-2/12',
        },
        assigned_to__name: {
          label: 'tablet',
          order: true,
          className: 'w-2/12',
        },
        type: {
          label: 'type',
          order: true,
          className: 'w-20',
        },
        tour_item_count: {
          label: 'tour_item_count',
          className: 'w-1/12',
        },
        actions: {
          label: '',
          order: false,
          className: 'w-12',
        },
      },
      perPage: 3000,

      loading: false,
    }
  },

  computed: {
    count() {
      return this.tourList && this.tourList.count ? this.tourList.count : 0
    },
    next() {
      return this.tourList && this.tourList.next ? this.tourList.next : null
    },
    previous() {
      return this.tourList && this.tourList.previous ? this.tourList.previous : null
    },
    results() {
      return this.tourList && this.tourList.results ? this.tourList.results : []
    },
  },

  methods: {
    updateState(event, item) {
      this.clicked = ''
      if (event === 'assign_tour') {
        return this.tabletAssignmentModal(item)
      }
      if (event === 'export_tour') {
        return this.exportTour(item)
      }
      if (event === 'discard_tour') {
        return this.discardTour(item)
      }
      if (event === 'simulate_readout') {
        this.selectedTourItem = item
        return (this.showModalSimulation = true)
      }
      if (event === 'unassign_tour') {
        return this.unAssignTour(item)
      }
    },
    closeTabletAssignmentModal(tabletName) {
      //console.log("selected tour state="+this.selectedTourItem.state)
      //console.log("event="+tabletName)
      this.selectedTourItem.state = 'Assigned'
      this.selectedTourItem.assigned_to = tabletName
    },
    async handleDrawerClick(clickedTour) {
      this.selectedTourItem = clickedTour
      const uuid = this.selectedTourItem.uuid
      await this.$store.dispatch('tours/updateSelectedTourData', {clickedTour})
      this.$router.push({
        name: 'TourDetails',
        params: {id: uuid, tourListPath: this.$router.currentRoute.fullPath},
      })
    },
    async getTours() {
      try {
        // if (this.onCreate) {
        //   this.onCreate = false
        //   return
        // }
        this.tourList = await this.$store.dispatch('tours/getTours')
      } catch (error) {}
    },
    async getToursDiscarded() {
      try {
        this.tourList = await this.$store.dispatch('tours/getToursDiscarded')
      } catch (error) {}
    },
    async restoreTour(tour) {
      this.clicked = ''
      try {
        await this.$store.dispatch('tours/restoreTour', tour)
        await this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: `restored`,
        })
        if (!this.$route.query.discarded) {
          tour.is_discarded = false
        } else {
          await this.getTours()
        }
      } catch (error) {
        console.log(error)
      }
    },
    async discardTour(tour) {
      try {
        await this.$store.dispatch('tours/discardTour', tour)
        await this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: `discarded`,
        })
        if (!this.$route.query.discarded) {
          tour.is_discarded = true
        } else {
          await this.getTours()
        }
      } catch (error) {
        console.log(error)
      }
    },
    async unAssignTour(tour) {
      if (
        confirm(
          this.$t(`mygwf.components.Confirmation.tour_confirm_unassign`, {
            name: tour.name,
            assigned_to: tour.assigned_to,
          })
        )
      ) {
        try {
          await this.$store.dispatch('tours/unAssignTablet', tour)
          tour.state = 'Open'
          tour.assigned_to = null
          await this.$store.dispatch('app/notification', {
            type: 'success',
            group: 'app',
            text: `unassigned`,
          })
        } catch (error) {
          console.log(error)
        }
      }
    },

    async exportTour(tour) {
      //console.log(tour.uuid)
      const uuid = tour.uuid
      this.isExporting = uuid
      try {
        const file = await this.$store.dispatch('tours/exportTour', uuid)
        const d = new Date()
        const extension =
          Object.keys(this.$store.getters['tours/supportedImportFormats']).find(
            key => this.$store.getters['tours/supportedImportFormats'][key] === file.type
          ) || 'txt'

        const filename = `${tour.name}-${d.toISOString().substring(0, 10)}.${extension}`
        fileExport(file, filename)
        this.$store.dispatch('app/notification', {
          type: 'success',
          group: 'app',
          text: `download`,
        })
        //await this.getTours()
      } catch (error) {
        console.log(error)
      } finally {
        this.isExporting = ''
      }
    },

    async deleteTour(tour) {
      this.clicked = ''
      this.isDeleting = tour.uuid
      if (
        confirm(
          this.$t(`mygwf.components.Confirmation.tour_confirm_delete`, {
            name: tour.name,
          })
        )
      ) {
        try {
          await this.$store.dispatch('tours/deleteTour', tour)
          await this.getTours()
          this.$store.dispatch('app/notification', {
            type: 'success',
            group: 'app',
            text: `deleted`,
          })
        } catch (error) {
          console.log(error)
          this.$store.dispatch('app/notification', {
            type: 'error',
            group: 'app',
            text: `deleted`,
          })
        } finally {
          this.isDeleting = ''
        }
      }
    },

    tabletAssignmentModal(tour) {
      console.log('Assign tour clicked', tour.name, Object.keys(tour))
      this.selectedTourItem = tour
      console.log(`selected tour item is ${Object.values(this.selectedTourItem)}`)
      this.showTabletAssignmentModal = true
    },

    stateColor(state) {
      switch (state) {
        case 'Open':
          return 'bg-secondary-dark'
        case 'In Progress':
          return 'bg-yellow-500'
        case 'Assigned':
          return 'bg-blue-400'
        case 'Completed':
          return 'bg-green-400'
        case 'Discarded':
          return 'bg-red-300'
      }
    },
    openMixTour(tour) {
      this.tablet = this.tablets.find(item => item.name === tour.assigned_to)
      this.showMixTour = true
    },
  },
}
</script>
<style>
.no-padding .ant-popover-inner-content {
  padding: 0;
}
</style>
